import { useCallback, useLayoutEffect } from 'react'
import { AccountTypeNames } from '@medentee/enums'
import { useHistory, useParams } from 'react-router'
import { stringify } from 'query-string'
import { useTranslation } from 'react-i18next'

import { TChannelVariant } from 'interfaces'
import { showModalAction, useAppDispatch, useAppSelector } from 'store'
import { EModalComponents } from 'App/containers/ModalRoot/ModalRoot.enums'

type TUseEventJoinViaLinkCoreProps = {
  variant: Extract<TChannelVariant, 'chat' | 'broadcast' | 'news'>
}

type TParams = {
  id?: string
  token?: string
}

type TRedirectToEventPayload = {
  title?: string | null
  channelId?: string
  isMember?: boolean
}

export const useEventJoinViaLinkCore = ({ variant }: TUseEventJoinViaLinkCoreProps) => {
  const dispatch = useAppDispatch()

  const { id = '', token = '' } = useParams<TParams>()

  const { replace } = useHistory()

  const { t } = useTranslation()

  const isNotProfessional = useAppSelector(
    (state) => state.global.accountData?.type.name !== AccountTypeNames.PROFESSIONAL
  )

  const redirectToDefaultPage = useCallback(() => replace('/'), [replace])

  const redirectToEvent = useCallback(
    ({ channelId, isMember, title }: TRedirectToEventPayload) => {
      if (isNotProfessional || !title) {
        return
      }

      if (isMember && channelId) {
        replace(`/events/${id}/${variant}/${channelId}`)
        return
      }

      replace({
        pathname: `/events/${id}/highlights/info`,
        search: stringify({ token, title, variant })
      })
    },
    [isNotProfessional, replace, id, token, variant]
  )

  useLayoutEffect(() => {
    if (isNotProfessional) {
      dispatch(
        showModalAction({
          modalType: EModalComponents.SWITCH_ACCOUNT_DIALOG,
          modalTitle: t('modal.switchAccountConfirm.title'),
          modalProps: {
            onSuccess: () => {
              window.location.href = `${window.location.origin}/join/events/${id}/${variant}/t/${token}`
            },
            onClose: redirectToDefaultPage
          }
        })
      )
    }
  }, [isNotProfessional, dispatch, redirectToDefaultPage, token, variant, id, t])

  return {
    id,
    token,
    isNotProfessional,
    redirectToDefaultPage,
    redirectToEvent
  }
}
